import type {
  EventChallenge,
  EventChallengeGroup,
  GroupUnlockCondition,
} from '@/config/objects/events';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Lightbulb, Layers } from 'lucide-react';
import { useEffect, useState } from 'react';

interface EventGroupUnlockEditorProps {
  eventId: string;
  group: EventChallengeGroup;
  allGroups: EventChallengeGroup[];
  allChallenges: EventChallenge[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
}

const CONDITION_OPTIONS: {
  value: GroupUnlockCondition;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: 'starter',
    label: 'Starter',
    description: 'Available from the start of the event',
    icon: Flag,
  },
  {
    value: 'after_challenge',
    label: 'After Challenge',
    description: 'Unlocks when a specific challenge is completed',
    icon: Lightbulb,
  },
  {
    value: 'after_group',
    label: 'After Group',
    description: 'Unlocks when all challenges in another group are completed',
    icon: Layers,
  },
];

export function EventGroupUnlockEditor({
  eventId,
  group,
  allGroups,
  allChallenges,
  open,
  onOpenChange,
  disabled = false,
}: EventGroupUnlockEditorProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const currentCondition = group.unlockConditions[0];

  const [conditionType, setConditionType] = useState<GroupUnlockCondition>('starter');
  const [triggerChallengeId, setTriggerChallengeId] = useState<string>('');
  const [triggerGroupId, setTriggerGroupId] = useState<string>('');

  // Filter out current group from selectable groups
  const otherGroups = allGroups.filter((g) => g.id !== group.id);

  // Reset form when dialog opens
  useEffect(() => {
    if (open && currentCondition) {
      setConditionType(currentCondition.conditionType);
      setTriggerChallengeId(currentCondition.triggerChallengeId || '');
      setTriggerGroupId(currentCondition.triggerGroupId || '');
    } else if (open) {
      setConditionType('starter');
      setTriggerChallengeId('');
      setTriggerGroupId('');
    }
  }, [open, currentCondition]);

  const mutation = useMutation({
    mutationFn: async (data: {
      conditionType: GroupUnlockCondition;
      triggerChallengeId?: string | null;
      triggerGroupId?: string | null;
    }) => {
      return apiClient.post(`/events/${eventId}/groups/${group.id}/unlock`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      toast.success('Unlock condition updated');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update unlock condition');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: {
      conditionType: GroupUnlockCondition;
      triggerChallengeId?: string | null;
      triggerGroupId?: string | null;
    } = {
      conditionType,
    };

    if (conditionType === 'after_challenge') {
      data.triggerChallengeId = triggerChallengeId || null;
    } else if (conditionType === 'after_group') {
      data.triggerGroupId = triggerGroupId || null;
    }

    mutation.mutate(data);
  };

  const isValid = () => {
    if (conditionType === 'starter') return true;
    if (conditionType === 'after_challenge') return !!triggerChallengeId;
    if (conditionType === 'after_group') return !!triggerGroupId;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Set Unlock Condition</DialogTitle>
            <DialogDescription>
              Configure when "{group.name}" becomes available to players
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Condition Type</Label>
              <div className="grid gap-2">
                {CONDITION_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = conditionType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setConditionType(option.value)}
                      disabled={disabled}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Icon className={`h-5 w-5 mt-0.5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div>
                        <div className={`font-medium ${isSelected ? 'text-primary' : ''}`}>
                          {option.label}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {conditionType === 'after_challenge' && (
              <div className="space-y-2">
                <Label htmlFor="triggerChallenge">Trigger Challenge</Label>
                <Select
                  value={triggerChallengeId}
                  onValueChange={setTriggerChallengeId}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a challenge..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allChallenges.map((challenge) => (
                      <SelectItem key={challenge.id} value={challenge.id}>
                        {challenge.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This group will unlock when the selected challenge is completed
                </p>
              </div>
            )}

            {conditionType === 'after_group' && (
              <div className="space-y-2">
                <Label htmlFor="triggerGroup">Trigger Group</Label>
                <Select
                  value={triggerGroupId}
                  onValueChange={setTriggerGroupId}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This group will unlock when all challenges in the selected group are completed
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid() || mutation.isPending || disabled}>
              {mutation.isPending ? 'Saving...' : 'Save Condition'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
